import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DangerBtn, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, panelGet, panelPost, useAdminGet } from '../kit';
import { Pill, type Tone, num, rowsOf, str } from '../ipintel/shared';
import { Modal } from '../email/emailKit';
import { type HzCtx, S, fmtCost, shortId } from './shared';

/**
 * Pending → Run (owner, 2026-10-11): runs horizon/scripts/horizon_batch_run_v2.py
 * on the API server, from here, on the pending technologies — the runner's
 * own only-missing selection, so exactly what the table below lists (minus
 * the technologies without a summary, which it never sends) — and keeps a log
 * of every run with its estimate and its actual cost.
 *
 * Backend: Backend/horizon_jobs.py through the dispatcher —
 *   GET  horizon/run_plan   what a run would do now: pending per university,
 *                           the prompt, which providers have a key on the
 *                           server (booleans, never a key — plus a note each:
 *                           where the key goes, Azure's warning), the
 *                           estimate, a suggested cost cap, the job and the
 *                           run log
 *   POST horizon/run_start  {provider, max_cost, limit?, refresh_current} →
 *                           {job}; refused when nothing is pending, the key is
 *                           missing, the cap is out of range or a run is going
 *   GET  horizon/run_job    {job, runs} — polled every 3 s while a run is going
 *   POST horizon/run_stop   Ctrl+C for the runner: the calls in flight finish
 *                           and are saved, nothing new starts (status stopped)
 *
 * The runner stops before the cap — once what it spent plus every call in
 * flight at its dearest (cap_headroom) could pass it; the cap is the only
 * hard limit, the estimate is just that — and is resume-safe
 * (a second run skips what the first finished). When it ends the server rebuilds horizon_current
 * — the data the public tech pages show — unless that was switched off. No
 * search index holds Horizon data, so nothing is ever reindexed.
 *
 * Every field is read defensively: the job and the log rows name the same
 * facts differently (by / started_by, max_cost / max_cost_usd, …).
 */

const MODEL = 'gpt-4.1';
const PROVIDERS = ['openai', 'azure'];
const PROVIDER_LABELS: Record<string, string> = { openai: 'OpenAI', azure: 'Azure OpenAI' };
/** Only when the server sends no note: the key goes into Backend/.env (System → Integrations is read-only). */
const keyHint = (k: string) => `${PROVIDER_LABELS[k] || k} has no key on the server, so a run would be refused — add `
  + `${k === 'azure' ? 'AZURE_OPENAI_API_KEY' : 'OPENAI_API_KEY'} to Backend/.env on the server and restart the API.`;
/** "≈ $X with caching (≈ $Y without)" — an estimate either way; only the cap is a limit. */
const estText = (cached: number | null, full: number | null) =>
  `≈ ${fmtCost(cached)} with caching${full !== null ? ` (≈ ${fmtCost(full)} without)` : ''}`;
const INDEX_NOTE = 'No search index holds Horizon data — only the public pages\' current data is refreshed.';
const DISMISS_KEY = 'utech_admin_horizon_run_dismissed';

const STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  starting: { label: 'Starting', tone: 'info', hint: 'The runner is starting on the server' },
  running: { label: 'Running', tone: 'info', hint: 'Classifying the pending technologies' },
  finishing: { label: 'Finishing', tone: 'info', hint: 'Reading the cost and refreshing the public pages' },
  done: { label: 'Done', tone: 'good', hint: 'Every planned technology was sent' },
  stopped_at_cap: { label: 'Stopped at the cap', tone: 'warn', hint: 'The cost cap was reached — the rest is still pending; run again to continue' },
  stopped: { label: 'Stopped', tone: 'warn', hint: 'Stopped before the end — the rest is still pending; run again to continue' },
  failed: { label: 'Failed', tone: 'bad', hint: 'The runner ended with an error' },
  interrupted: { label: 'Interrupted', tone: 'bad', hint: 'The runner went away without finishing (a server restart?)' },
};
/** Statuses that are not final yet — keep polling through them. */
const LIVE = ['starting', 'running', 'finishing', 'refreshing'];

const isNum = (v: any): boolean => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const numOrNull = (v: any): number | null => (isNum(v) ? Number(v) : null);
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const fmtSecs = (s: number): string => {
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) { const m = Math.floor(s / 60); const r = Math.round(s - m * 60); return r ? `${m} min ${r} s` : `${m} min`; }
  const h = Math.floor(s / 3600);
  return `${h} h ${Math.round((s - h * 3600) / 60)} min`;
};
const secsBetween = (a: string, b: string): number | null => {
  const t0 = Date.parse(a), t1 = Date.parse(b);
  return Number.isFinite(t0) && Number.isFinite(t1) ? Math.max(0, (t1 - t0) / 1000) : null;
};
/** The runner prints "ETA 4m"; a number is seconds. */
const fmtEta = (v: any): string => (isNum(v) ? fmtSecs(Number(v)) : str(v).trim());

// ---------------------------------------------------------------------------
// One run — the live job or a log row — under one set of names.
// ---------------------------------------------------------------------------
type Run = {
  id: string; running: boolean; status: string; started_at: string; finished_at: string; by: string;
  provider: string; model: string; total: number | null; limit: number | null; planned: number | null; processed: number | null;
  ok: number | null; failed: number | null; so_far: number | null; actual: number | null; cap: number | null;
  est_cached: number | null; est_full: number | null; tokens: number | null; run_id: string; eta: any;
  refreshed: any; refresh_result: any; asked_refresh: boolean | null; stop_requested: boolean; error: string; log: string[];
};

const normRun = (x: any): Run => {
  const status = str(x?.status);
  return {
    id: str(x?.id),
    running: typeof x?.running === 'boolean' ? x.running : LIVE.includes(status),
    status: status || (x?.running ? 'running' : ''),
    started_at: str(x?.started_at), finished_at: str(x?.finished_at),
    by: str(x?.by ?? x?.started_by),
    provider: str(x?.provider), model: str(x?.model),
    total: numOrNull(x?.total ?? x?.pending_before), limit: numOrNull(x?.limit ?? x?.limit_n), planned: numOrNull(x?.planned),
    processed: numOrNull(x?.processed), ok: numOrNull(x?.ok), failed: numOrNull(x?.failed),
    so_far: numOrNull(x?.cost_so_far), actual: numOrNull(x?.actual_cost_usd ?? x?.actual_cost),
    cap: numOrNull(x?.max_cost ?? x?.max_cost_usd),
    est_cached: numOrNull(x?.estimate_cached ?? x?.est_cached_usd), est_full: numOrNull(x?.estimate_full ?? x?.est_full_usd),
    tokens: numOrNull(x?.tokens ?? x?.total_tokens), run_id: str(x?.run_id), eta: x?.eta,
    refreshed: x?.refreshed_current, refresh_result: x?.refresh_result,
    asked_refresh: typeof x?.refresh_current === 'boolean' ? x.refresh_current
      : typeof x?.refresh_requested === 'boolean' ? x.refresh_requested : null,
    stop_requested: !!x?.stop_requested,
    error: str(x?.error),
    log: Array.isArray(x?.log_tail) ? x.log_tail.map(str) : [],
  };
};

/** Fields of b over a, skipping the empty ones (a live job has no log-row columns and vice versa). */
const merge = (a: any, b: any) => {
  const out: Record<string, any> = { ...(a || {}) };
  Object.entries(b || {}).forEach(([k, v]) => { if (v !== null && v !== undefined) out[k] = v; });
  return out;
};

/** How many technologies the run set out to do (the runner's own count once it printed it). */
const plannedOf = (r: Run): number | null =>
  r.planned ?? (r.limit && r.total !== null ? Math.min(r.limit, r.total) : r.limit || r.total);

/** What a run's cost is: the actual once it ended, else the running figure. */
const costOf = (r: Run): number | null => r.actual ?? r.so_far;

type Refresh = { state: 'ok' | 'failed' | 'waiting' | 'no'; rows: number | null; runRows: number | null; text: string };

/** The horizon_current rebuild after a run: store.refresh_current()'s
 *  {rows, run_rows (this run's), by_prompt, took_ms} | {skipped} | {error},
 *  possibly wrapped in {refresh}. */
const refreshOf = (r: Run): Refresh => {
  let res = r.refresh_result ?? (r.refreshed && typeof r.refreshed === 'object' ? r.refreshed : null);
  if (typeof res === 'string') { try { res = JSON.parse(res); } catch { res = { error: res }; } }
  if (res && typeof res === 'object' && res.refresh && typeof res.refresh === 'object') res = res.refresh;
  const none = { rows: null, runRows: null };
  if (res && typeof res === 'object') {
    if (res.error) return { state: 'failed', ...none, text: str(res.error) };
    if (res.skipped) return { state: 'failed', ...none, text: `not rebuilt: ${str(res.skipped)}` };
    if (r.refreshed === false) return { state: 'failed', ...none, text: 'not rebuilt' };
    return { state: 'ok', rows: numOrNull(res.rows), runRows: numOrNull(res.run_rows), text: '' };
  }
  if (r.refreshed === true) return { state: 'ok', ...none, text: '' };
  if (r.running && r.asked_refresh !== false) return { state: 'waiting', ...none, text: '' };
  return { state: 'no', ...none, text: r.asked_refresh === false ? 'switched off for this run' : '' };
};

const StatusPill: React.FC<{ r: Run }> = ({ r }) => {
  const m = STATUS[r.status];
  const title = [m?.hint, r.error].filter(Boolean).join(' — ');
  return <Pill tone={m?.tone || (r.running ? 'info' : 'neutral')} title={title || undefined}>{m?.label || r.status || (r.running ? 'Running' : '—')}</Pill>;
};

/** A thin bar; tone picks the fill. */
const Bar: React.FC<{ value: number; max: number; tone?: 'primary' | 'warn' | 'bad'; title?: string }> =
  ({ value, max, tone = 'primary', title }) => {
  const pctW = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const fill = tone === 'bad' ? 'bg-red-500' : tone === 'warn' ? 'bg-amber-400' : 'bg-primary-500';
  return (
    <div className="bg-grayscale-200 rounded-full h-2 overflow-hidden" title={title}>
      <div className={`h-2 rounded-full transition-all ${fill}`} style={{ width: `${pctW}%` }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------
const HzRunPending: React.FC<{
  ctx: HzCtx;
  /** horizon/pending's `runner` — whether the prompt the runner would use is the FINAL one. */
  runner?: any;
  /** Narrows the pending table below to a university (the chips). */
  onUniversity?: (code: string) => void;
  university?: string;
  /** Told whether a run is going (Pending hides its command-line run meanwhile). */
  onLive?: (live: boolean) => void;
}> = ({ ctx, runner, onUniversity, university, onLive }) => {
  const plan = useAdminGet(S, 'run_plan', {}, [ctx.dataTick]);
  const p = plan.data || {};

  // The job and the log come from run_job (polled while a run is going); the
  // plan's own copies are used on arrival and when they are news.
  const [jobRaw, setJobRaw] = useState<any>(null);
  const [runsRaw, setRunsRaw] = useState<any[]>([]);
  const [jobErr, setJobErr] = useState('');
  const polling = useRef(false);
  // Bumped by a start / stop too: a poll that left before it answers with an older job.
  const seq = useRef(0);
  const loadJob = useCallback(async () => {
    if (polling.current) return;               // a slow answer must not stack requests
    polling.current = true;
    const mine = ++seq.current;
    const { data, error } = await panelGet(S, 'run_job');
    polling.current = false;
    if (mine !== seq.current) return;
    setJobErr(error);
    if (data) {
      if (data.job !== undefined) setJobRaw(data.job || null);
      if (Array.isArray(data.runs)) setRunsRaw(data.runs);
    }
  }, []);
  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => {
    const pj = p.job;
    if (pj && typeof pj === 'object') {
      setJobRaw((cur: any) => (!cur || (pj.running && !cur.running && str(pj.id) !== str(cur.id)) ? pj : cur));
    }
    if (Array.isArray(p.runs)) setRunsRaw(p.runs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.data]);

  const runs = useMemo(() => runsRaw.map(normRun), [runsRaw]);
  const job = useMemo(() => {
    if (!jobRaw) return null;
    const row = runsRaw.find((r: any) => str(r?.id) && str(r?.id) === str(jobRaw.id));
    return normRun(merge(row, jobRaw));
  }, [jobRaw, runsRaw]);
  const live = !!job && (job.running || LIVE.includes(job.status));

  // Every 3 s while a run is going (and through its finishing step).
  useEffect(() => {
    if (!live) return;
    const t = window.setInterval(loadJob, 3000);
    return () => window.clearInterval(t);
  }, [live, loadJob]);
  // When it ends, everything moved: pending, the overview, the runs, the current data.
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !live) ctx.reloadAll();
    wasLive.current = live;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  useEffect(() => { onLive?.(live); }, [live, onLive]);

  // A finished run stays on screen until dismissed (per browser, by its id).
  const [dismissed, setDismissed] = useState<string>(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });
  const jobKey = job ? job.id || job.started_at : '';
  const dismiss = () => {
    setDismissed(jobKey);
    try { localStorage.setItem(DISMISS_KEY, jobKey); } catch { /* private mode */ }
  };
  const showJob = !!job && !!jobKey && (live || jobKey !== dismissed);

  // ---- The plan ---------------------------------------------------------------
  const pend = p.pending || {};
  const total = num(pend.total);
  const noSummary = num(pend.no_summary);
  const perUni = rowsOf(pend.per_university).map((u: any) => ({ code: str(u?.code), name: str(u?.name) || str(u?.code), n: num(u?.pending ?? u?.n) }))
    .filter((u: any) => u.code && u.n > 0).sort((a: any, b: any) => b.n - a.n);
  const providers: Record<string, any> = p.providers && typeof p.providers === 'object' ? p.providers : {};
  const provList = Array.from(new Set([...PROVIDERS, ...Object.keys(providers)]));
  const readyOf = (k: string) => !!providers[k]?.ready;
  // Never Azure on its own: the server's default (OpenAI) even without a key —
  // Start stays off until a ready provider is picked.
  const defProv = str(p.default_provider);
  const [provPick, setProvPick] = useState('');
  const prov = provPick || (defProv && defProv !== 'azure' ? defProv : 'openai');
  const provReady = readyOf(prov);
  // The server's words: where the key goes (note), and what to know first (warning — Azure's).
  const provNote = str(providers[prov]?.note);
  const provWarn = str(providers[prov]?.warning);
  const model = str(p.model) || MODEL;
  const est = p.estimate || {};
  const perTech = isNum(est.per_tech_cached) ? Number(est.per_tech_cached) : total ? num(est.usd_cached) / total : 0;
  const perTechFull = isNum(est.per_tech_full) ? Number(est.per_tech_full) : total ? num(est.usd_full) / total : 0;
  // The runner stops once spent + every call in flight at its dearest could pass the cap.
  const headroom = num(p.cap_headroom);
  const maxCap = numOrNull(p.max_cap);
  const suggested = numOrNull(p.suggested_cap);

  const [capIn, setCapIn] = useState<string | null>(null);
  const cap = capIn ?? (suggested !== null ? String(suggested) : '');
  const [limit, setLimit] = useState('');
  const [refresh, setRefresh] = useState(true);
  const capNum = Number(cap);
  const capErr = !cap.trim() || !Number.isFinite(capNum) || capNum <= 0 ? 'Enter the cost cap in US dollars (more than 0).'
    : maxCap !== null && capNum > maxCap ? `At most ${fmtCost(maxCap)} per run.`
    : headroom > 0 && capNum <= headroom ? `Set more than ${fmtCost(headroom)}: the runner stops once its spend plus the calls in flight (up to ${fmtCost(headroom)}) could reach the cap, so a lower cap ends the run after its first few calls. For a small pilot, set a limit instead.` : '';
  const limitNum = limit.trim() ? Number(limit) : null;
  const limitErr = limitNum !== null && (!Number.isInteger(limitNum) || limitNum < 1)
    ? 'The limit is a whole number of technologies (1 or more) — or leave it empty for all.' : '';
  const n = limitNum && !limitErr ? Math.min(limitNum, total) : total;
  const estCached = limitNum && !limitErr ? perTech * n : num(est.usd_cached);
  const estFull = limitNum && !limitErr ? perTechFull * n : num(est.usd_full);
  // How far the cap reaches at the cached rate, when it is below the estimate.
  const usable = capNum - headroom;
  const capReach = !capErr && perTech > 0 && usable < estCached ? Math.max(0, Math.floor(usable / perTech)) : null;

  const prompt = p.prompt || {};
  const promptId = str(prompt.id);
  const pInfo = promptId ? ctx.promptById(promptId) : undefined;
  const isFinal = typeof prompt.is_final === 'boolean' ? prompt.is_final
    : !!pInfo?.is_final || (!!runner?.prompt_is_final && str(runner?.prompt_id) === promptId);
  const filesMissing = !!prompt.files_missing;

  const why = !plan.data ? '' : live ? 'A run is going — wait for it to end.'
    : filesMissing ? 'The prompt files are missing on the server.'
    : !total ? 'Nothing is pending.'
    : !provReady ? `${PROVIDER_LABELS[prov] || prov} has no key on the server.` : '';
  // (A bad cap or limit is said under the inputs.)
  const canStart = !!plan.data && !why && !capErr && !limitErr;

  const [confirm, setConfirm] = useState(false);
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const start = async () => {
    if (!canStart) return;
    setStarting(true); setMsg(null);
    const body: Record<string, any> = { provider: prov, max_cost: capNum, refresh_current: refresh };
    if (limitNum) body.limit = limitNum;
    const { data, error } = await panelPost(S, 'run_start', body);
    setStarting(false);
    setConfirm(false);
    if (error) {
      setMsg({ kind: 'error', text: error });
      loadJob();                                // most likely: a run is already going — show it
      return;
    }
    seq.current++;
    if (data?.job) setJobRaw(data.job);
    loadJob();
  };

  // Ctrl+C for the runner: what is in flight finishes and is saved.
  const stop = async (): Promise<string> => {
    const { data, error } = await panelPost(S, 'run_stop');
    seq.current++;
    if (data?.job) setJobRaw(data.job);
    loadJob();
    return error;
  };

  const provLabel = (k: string) => `${PROVIDER_LABELS[k] || k} — ${readyOf(k) ? 'ready' : 'no key'}${str(providers[k]?.warning) ? ' ⚠' : ''}`;
  const provSource = str(providers[prov]?.source);

  return (
    <>
      <Section title="Run Horizon for the pending technologies"
        right={<Btn small onClick={() => { plan.reload(); loadJob(); }} disabled={plan.loading}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Runs the batch runner on the server, in the background, on every pending technology — one model call each,
          paid. It stops before the cost cap, and it is resume-safe: a stopped run is continued by running again
          (what is done is skipped). You can leave this page while it runs.
        </p>
        {plan.error && <Msg kind="error">{plan.error}</Msg>}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {plan.loading && !plan.data ? <Loading label="Counting what a run would do…" /> : plan.data && (
          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex items-baseline gap-x-3 gap-y-1 flex-wrap">
              <span className="text-2xl font-bold text-grayscale-1300 font-grotesk tabular-nums">{total.toLocaleString()}</span>
              <span className="text-sm text-grayscale-1200">technolog{total === 1 ? 'y' : 'ies'} to run</span>
              {noSummary > 0 && (
                <span className="text-xs text-grayscale-1100"
                  title="The runner never sends a technology without a summary — fix its summary first">
                  · {noSummary.toLocaleString()} more without a summary are never sent
                </span>
              )}
            </div>
            {perUni.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {perUni.map((u: any) => {
                  const on = university === u.code;
                  return (
                    <button key={u.code} type="button" disabled={!onUniversity}
                      onClick={() => onUniversity?.(on ? '' : u.code)}
                      title={on ? 'The table below shows only this university — click to clear' : `${u.name} — show these in the table below`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap ${on
                        ? 'bg-primary-500 border-primary-500 text-grayscale-100 font-bold'
                        : 'bg-white border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                      {u.name}<span className={on ? '' : 'text-amber-700 font-medium'}>{u.n.toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="text-xs text-grayscale-1200">
              Prompt:{' '}
              {promptId ? (
                <>
                  <button type="button" className="underline" onClick={() => ctx.goPrompt(promptId)}>
                    {str(prompt.name) || shortId(promptId)}
                  </button>
                  {isFinal ? <span className="text-green-700"> — the FINAL prompt ✓</span>
                    : (typeof prompt.on_pages === 'boolean' ? prompt.on_pages : !!pInfo) ? <span className="text-amber-700"> — not the FINAL prompt ⚠ (its results would only fill gaps as a fallback)</span> : null}
                </>
              ) : <span className="font-medium">a new prompt version (the prompt files match no saved prompt)</span>}
            </div>
            {str(p.warning) && (
              <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">{str(p.warning)}</div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 min-w-0">
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-grayscale-1100">Provider</span>
                <Select value={prov} onChange={setProvPick} options={provList}
                  labels={Object.fromEntries(provList.map(k => [k, provLabel(k)]))} />
                <span className="text-[11px] text-grayscale-1100">
                  {!provReady ? 'No key on the server — a run would be refused'
                    : provSource === 'env' ? 'Key set in the server environment'
                    : provSource === 'saved' ? 'Key saved in the admin config' : 'Key found on the server'}
                </span>
              </label>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-grayscale-1100">Model</span>
                <span className="h-9 px-3 rounded-md border-[0.5px] border-grayscale-300 bg-grayscale-200 text-sm flex items-center font-mono"
                  title="Fixed: the model the final prompt was made for">{model}</span>
              </div>
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-grayscale-1100">Cost cap (USD)</span>
                <TextInput value={cap} onChange={setCapIn} type="number"
                  placeholder={suggested !== null ? `e.g. ${suggested}` : 'e.g. 10'} />
                <span className="text-[11px] text-grayscale-1100"
                  title={headroom > 0 ? `It stops once what it spent plus the calls in flight (≈ ${fmtCost(headroom)} at most) could pass the cap` : undefined}>
                  The only hard limit: the run stops before it could pass {capErr ? 'the cap' : fmtCost(capNum)}
                  {suggested !== null ? ` · suggested ${fmtCost(suggested)}` : ''}
                  {maxCap !== null ? ` · ${fmtCost(maxCap)} the highest allowed` : ''}
                </span>
              </label>
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-grayscale-1100">Limit (optional)</span>
                <TextInput value={limit} onChange={setLimit} type="number" placeholder={`all ${total.toLocaleString()}`} />
                <span className="text-[11px] text-grayscale-1100">Only the first N pending — e.g. 50 for a pilot</span>
              </label>
            </div>

            {(capErr || limitErr) && plan.data && total > 0 && (
              <div className="text-xs text-red-600">{capErr || limitErr}</div>
            )}

            {provWarn && (
              <div className="text-sm rounded-lg px-3 py-2 border text-red-700 bg-red-50 border-red-200">
                <span className="font-bold">⚠ {PROVIDER_LABELS[prov] || prov}:</span> {provWarn}
              </div>
            )}
            {/* The server's note says where the key goes; Azure's is shown even with a key. */}
            {(!provReady || (prov === 'azure' && provNote)) ? (
              <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200 break-words">
                {provNote || keyHint(prov)}
              </div>
            ) : provNote ? <div className="text-xs text-grayscale-1100 break-words">{provNote}</div> : null}

            <label className="flex items-start gap-2 text-sm text-grayscale-1200 cursor-pointer w-fit">
              <input type="checkbox" className="mt-1" checked={refresh} onChange={e => setRefresh(e.target.checked)} />
              <span>
                Refresh the public pages when done{' '}
                <span className="text-xs text-grayscale-1100">(rebuilds their current data — the same as Overview → Refresh current data)</span>
              </span>
            </label>

            {total > 0 ? (
              <div className="rounded-lg border border-grayscale-300 bg-grayscale-200 px-3 py-2 flex flex-col gap-1 text-sm text-grayscale-1300">
                <div>
                  Estimate for {plural(n, 'technology', 'technologies')}:{' '}
                  <span className="font-bold">≈ {fmtCost(estCached)}</span> with caching{' '}
                  <span className="text-grayscale-1200">(≈ {fmtCost(estFull)} without)</span>
                  {perTech > 0 && <span className="text-xs text-grayscale-1100"> · ≈ {fmtCost(perTech, true)} per technology</span>}
                </div>
                {!capErr && (
                  <div className="text-xs text-grayscale-1200">
                    An estimate, not a limit — the only hard limit is the cap: the run stops before it could
                    pass <span className="font-bold">{fmtCost(capNum)}</span>.
                  </div>
                )}
                {capReach !== null && (
                  <div className="text-xs text-amber-700">
                    The cap is below the estimate: the run stops after about {plural(capReach, 'technology', 'technologies')} and
                    leaves the rest pending — run again later to continue.
                  </div>
                )}
                {capReach === null && !capErr && estFull > 0 && usable < estFull && (
                  <div className="text-xs text-grayscale-1100">
                    Enough at the cached rate; if caching does not hold, the run stops before the cap and the rest stays pending.
                  </div>
                )}
                <div className="text-xs text-grayscale-1100">
                  Search indexes: nothing to rebuild. {str(p.index_note) || INDEX_NOTE}
                </div>
              </div>
            ) : (
              <Msg kind="ok">Nothing to run — every technology with a summary has a Horizon result under this prompt.</Msg>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <Btn kind="primary" onClick={() => setConfirm(true)} disabled={!canStart || starting}>
                {live ? 'A run is going…' : n ? `Run ${plural(n, 'technology', 'technologies')}…` : 'Nothing to run'}
              </Btn>
              {why && total > 0 && <span className="text-xs text-grayscale-1100">{why}</span>}
            </div>
          </div>
        )}
      </Section>

      {/* Keyed by the run: its refresh / stop messages never carry over to the next one. */}
      {showJob && job && <JobCard key={jobKey} job={job} ctx={ctx} onDismiss={dismiss} onStop={stop} />}

      <RunLog runs={runs} ctx={ctx} error={jobErr} onRefresh={loadJob} />

      {confirm && (
        <Modal title={`Run Horizon on ${plural(n, 'technology', 'technologies')}?`} onClose={() => !starting && setConfirm(false)}
          footer={<>
            <Btn onClick={() => setConfirm(false)} disabled={starting}>Cancel</Btn>
            <Btn kind="primary" onClick={start} disabled={starting || !canStart}>
              {starting ? 'Starting…' : `Start — cap ${fmtCost(capNum)}`}
            </Btn>
          </>}>
          {provWarn && (
            <div className="text-sm rounded-lg px-3 py-2 border text-red-700 bg-red-50 border-red-200">
              <span className="font-bold">⚠ {PROVIDER_LABELS[prov] || prov}:</span> {provWarn}
            </div>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-grayscale-1100">Technologies</dt>
            <dd>{n.toLocaleString()}{limitNum ? ` (limit — of ${total.toLocaleString()} pending)` : ' — every pending one'}</dd>
            <dt className="text-grayscale-1100">Provider · model</dt>
            <dd>{PROVIDER_LABELS[prov] || prov} · <span className="font-mono">{model}</span></dd>
            <dt className="text-grayscale-1100">Prompt</dt>
            <dd>{str(prompt.name) || shortId(promptId) || 'a new prompt version'}{isFinal ? ' (FINAL)' : ''}</dd>
            <dt className="text-grayscale-1100">Estimate</dt>
            <dd>{estText(estCached, estFull)}</dd>
            <dt className="text-grayscale-1100">Cost cap</dt>
            <dd>
              <span className="font-bold">{fmtCost(capNum)}</span>
              <span className="text-grayscale-1100"> — the only hard limit: the run stops before it could pass it</span>
            </dd>
            <dt className="text-grayscale-1100">Public pages</dt>
            <dd>{refresh ? 'refreshed when it finishes' : 'not refreshed — press Refresh current data afterwards'}</dd>
          </dl>
          <p className="text-xs text-grayscale-1100">
            This calls the model once per technology and costs real money. It runs on the server in the background; the
            results are saved as it goes, so stopping (or reaching the cap) loses nothing and a second run continues where
            this one ended. No search index needs rebuilding afterwards.
          </p>
        </Modal>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// The run in progress (or the last one, until dismissed)
// ---------------------------------------------------------------------------
const JobCard: React.FC<{ job: Run; ctx: HzCtx; onDismiss: () => void; onStop: () => Promise<string> }> =
  ({ job, ctx, onDismiss, onStop }) => {
  const live = job.running || LIVE.includes(job.status);
  const planned = plannedOf(job);
  const processed = job.processed ?? 0;
  const cost = costOf(job);
  const rf = refreshOf(job);
  const end = job.finished_at || (live ? new Date().toISOString() : '');
  const took = job.started_at && end ? secsBetween(job.started_at, end) : null;

  // Follow the log as it grows, unless someone scrolled up to read it.
  const box = useRef<HTMLPreElement | null>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [job.log.length, job.log[job.log.length - 1]]);

  const [busy, setBusy] = useState(false);
  const [rmsg, setRmsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const refreshNow = async () => {
    setBusy(true); setRmsg(null);
    const { data, error } = await panelPost(S, 'refresh_current');
    setBusy(false);
    if (error) { setRmsg({ kind: 'error', text: `Refresh failed: ${error}` }); return; }
    const res = data?.refresh && typeof data.refresh === 'object' ? data.refresh : (data || {});
    if (res.skipped) { setRmsg({ kind: 'error', text: `Not rebuilt: ${str(res.skipped)}` }); return; }
    setRmsg({ kind: 'ok', text: `Public pages refreshed ✓ (${num(res.rows).toLocaleString()} rows)` });
    ctx.reloadAll();
  };

  const [stopErr, setStopErr] = useState('');
  const [stopping, setStopping] = useState(false);
  const stopNow = async () => {
    setStopping(true); setStopErr('');
    setStopErr(await onStop());
    setStopping(false);
  };
  const canStop = job.status === 'running' && !job.stop_requested;

  const capTone = job.cap && cost !== null && cost >= job.cap * 0.9 ? 'warn' : 'primary';

  return (
    <Section title={live ? 'Horizon run in progress' : 'Last Horizon run'}
      right={
        <div className="flex items-center gap-2">
          <StatusPill r={job} />
          {canStop && (stopping ? <Btn small disabled>Stopping…</Btn>
            : <DangerBtn small onConfirm={stopNow}>Stop the run</DangerBtn>)}
          {!live && <Btn small onClick={onDismiss} title="Hide this until the next run (it stays in the run log)">Dismiss</Btn>}
        </div>
      }>
      <div className="text-xs text-grayscale-1100 -mt-1">
        Started by {job.by || 'an admin'} · {fmtDate(job.started_at)}
        {job.finished_at ? <> · finished {fmtDate(job.finished_at)}</> : null}
        {took !== null ? <> · {live ? 'running for' : 'took'} {fmtSecs(took)}</> : null}
        {' · '}{PROVIDER_LABELS[job.provider] || job.provider || '—'} · <span className="font-mono">{job.model || MODEL}</span>
        {job.run_id && (
          <> · run{' '}
            <button type="button" className="underline font-mono" title="Open this run's results"
              onClick={() => ctx.goResults({ run: job.run_id })}>{shortId(job.run_id)}</button>
          </>
        )}
        {live && <> · refreshes every 3 s</>}
      </div>
      {stopErr && <Msg kind="error">Stop failed: {stopErr}</Msg>}
      {live && job.stop_requested && (
        <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
          Stopping — the calls in flight finish and are saved; nothing new starts. Run again later to continue.
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex justify-between gap-3 flex-wrap text-xs text-grayscale-1200">
          <span>
            <b className="text-grayscale-1300">{processed.toLocaleString()}</b>
            {planned !== null ? ` of ${planned.toLocaleString()}` : ''} technologies
            {job.ok !== null && <> · <span className="text-green-700">{job.ok.toLocaleString()} ok</span></>}
            {job.failed !== null && <> · <span className={job.failed ? 'text-red-600 font-medium' : ''}>{job.failed.toLocaleString()} failed</span></>}
          </span>
          {live && fmtEta(job.eta) && <span>ETA {fmtEta(job.eta)}</span>}
        </div>
        <Bar value={processed} max={planned || 0} tone={job.status === 'failed' ? 'bad' : 'primary'}
          title={planned ? `${Math.round((processed / planned) * 100)}%` : undefined} />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex justify-between gap-3 flex-wrap text-xs text-grayscale-1200">
          <span>
            {live ? 'Cost so far' : 'Actual cost'} <b className="text-grayscale-1300">{fmtCost(cost)}</b>
            {job.cap !== null && <> of the {fmtCost(job.cap)} cap</>}
            {job.cap !== null && live && <span className="text-grayscale-1100"> — it stops before it could pass it</span>}
          </span>
          {(job.est_cached !== null || job.est_full !== null) && (
            <span className="text-grayscale-1100">estimate {estText(job.est_cached, job.est_full)}</span>
          )}
        </div>
        {job.cap ? <Bar value={cost || 0} max={job.cap} tone={capTone} /> : null}
      </div>

      {!live && (
        <div className="flex flex-col gap-2">
          {job.status === 'done' && (
            <Msg kind="ok">
              Finished: {plural(processed, 'technology', 'technologies')} sent{job.failed ? `, ${plural(job.failed, 'failure')}` : ''}.
              Actual cost {fmtCost(cost)}{job.est_cached !== null ? ` against an estimate of ≈ ${fmtCost(job.est_cached)}` : ''}
              {job.tokens !== null ? ` · ${job.tokens.toLocaleString()} tokens` : ''}.
            </Msg>
          )}
          {(job.status === 'stopped_at_cap' || job.status === 'stopped') && (
            <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
              {job.status === 'stopped_at_cap' ? `Stopped at the ${fmtCost(job.cap)} cap` : 'Stopped'} after{' '}
              {plural(processed, 'technology', 'technologies')} — actual cost {fmtCost(cost)}. The rest is still pending:
              run again to continue (what is done is skipped).
            </div>
          )}
          {(job.status === 'failed' || job.status === 'interrupted') && (
            <Msg kind="error">
              The run {job.status === 'failed' ? 'failed' : 'was interrupted'}{job.error ? `: ${job.error}` : '.'}
              {cost !== null ? ` Cost until then ${fmtCost(cost)}.` : ''} What it finished is saved; run again to continue.
            </Msg>
          )}
          {['done', 'stopped_at_cap', 'stopped'].includes(job.status) && job.error && (
            <div className="text-xs text-amber-700 break-words">{job.error}</div>
          )}
          {!['done', 'stopped_at_cap', 'stopped', 'failed', 'interrupted'].includes(job.status) && job.error && (
            <Msg kind="error">{job.error}</Msg>
          )}

          {rmsg ? <Msg kind={rmsg.kind}>{rmsg.text}</Msg>
            : rf.state === 'ok' ? (
              <div className="text-sm text-green-700">
                Public pages refreshed ✓{rf.rows !== null ? ` (${rf.rows.toLocaleString()} rows${rf.runRows !== null ? `, ${rf.runRows.toLocaleString()} from this run` : ''})` : ''}
              </div>
            ) : (
              <div className={`text-sm rounded-lg px-3 py-2 border flex items-center justify-between gap-3 flex-wrap ${rf.state === 'failed'
                ? 'text-red-600 bg-red-50 border-red-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
                <span>
                  {rf.state === 'failed'
                    ? `The public pages were not refreshed: ${rf.text}.`
                    : `The public pages were not refreshed${rf.text ? ` (${rf.text})` : ''}.`}
                  {' '}Press Refresh current data so the tech pages show the new results.
                </span>
                <Btn small kind="primary" onClick={refreshNow} disabled={busy}
                  title="Rebuild horizon_current (one row per technology) from the results now">
                  {busy ? 'Rebuilding…' : 'Refresh current data'}
                </Btn>
              </div>
            )}
        </div>
      )}
      {live && rf.state === 'waiting' && (
        <div className="text-xs text-grayscale-1100">The public pages are refreshed when the run ends.</div>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-grayscale-1100">Runner output (last {plural(job.log.length, 'line')})</span>
        <pre ref={box}
          onScroll={e => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          className="max-h-64 overflow-auto rounded-lg border border-grayscale-300 bg-grayscale-200 px-3 py-2
            text-[11px] leading-relaxed font-mono text-grayscale-1200 whitespace-pre-wrap break-words">
          {job.log.length ? job.log.join('\n') : 'No output yet.'}
        </pre>
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Run log — horizon_admin_runs, newest first: estimate against actual.
// ---------------------------------------------------------------------------
const LOG_COLUMNS = ['started_at', 'by', 'provider', 'techs', 'estimate', 'cap', 'actual', 'status', 'refreshed'];
const LOG_LABELS: Record<string, string> = {
  started_at: 'Started', by: 'By', provider: 'Provider / model', techs: 'Techs', estimate: 'Estimate',
  cap: 'Cap', actual: 'Actual cost', status: 'Status', refreshed: 'Pages refreshed',
};

const RunLog: React.FC<{ runs: Run[]; ctx: HzCtx; error: string; onRefresh: () => void }> =
  ({ runs, ctx, error, onRefresh }) => {
  // Flat values for sorting and the CSV; the rendering reads _r.
  const rows = useMemo(() => runs.map(r => {
    const rf = refreshOf(r);
    return {
      started_at: r.started_at, by: r.by, provider: [PROVIDER_LABELS[r.provider] || r.provider, r.model].filter(Boolean).join(' · '),
      techs: r.processed, estimate: r.est_cached, cap: r.cap, actual: costOf(r), status: STATUS[r.status]?.label || r.status,
      refreshed: rf.state === 'ok' ? (rf.rows !== null ? rf.rows : 'yes') : rf.state === 'waiting' ? '' : 'no',
      run_id: r.run_id, _r: r,
    };
  }), [runs]);
  const spent = runs.reduce((a, r) => a + (costOf(r) || 0), 0);

  return (
    <Section title={`Run log (${runs.length})`}
      right={
        <div className="flex items-center gap-2 flex-wrap">
          {runs.length > 0 && <span className="text-xs text-grayscale-1100">{fmtCost(spent)} spent in these runs</span>}
          <Btn small onClick={onRefresh}>Refresh</Btn>
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Every run started from this page (the last 20), newest first: what it was estimated to cost before it started and
        what it actually cost. Runs started from the server's command line are not here — they are under Runs.
      </p>
      {error && <Msg kind="error">{error}</Msg>}
      <DataTable rows={rows} columns={LOG_COLUMNS} labels={LOG_LABELS} csvName="horizon_admin_runs" maxHeight="20rem"
        empty="No run started from here yet."
        render={{
          started_at: row => {
            const r: Run = row._r;
            const took = r.started_at && r.finished_at ? secsBetween(r.started_at, r.finished_at) : null;
            return (
              <span className="flex flex-col leading-tight whitespace-nowrap">
                <span className="text-xs">{fmtDate(r.started_at)}</span>
                {took !== null && <span className="text-[10px] text-grayscale-1100">took {fmtSecs(took)}</span>}
              </span>
            );
          },
          by: row => <span className="block max-w-[12rem] truncate text-xs" title={str(row.by)}>{str(row.by) || '—'}</span>,
          provider: row => {
            const r: Run = row._r;
            return <span className="text-xs whitespace-nowrap">{PROVIDER_LABELS[r.provider] || r.provider || '—'}{r.model ? <span className="text-grayscale-1100 font-mono"> · {r.model}</span> : null}</span>;
          },
          techs: row => {
            const r: Run = row._r;
            const planned = plannedOf(r);
            return (
              <span className="flex flex-col leading-tight whitespace-nowrap tabular-nums"
                title={`${r.total !== null ? `${r.total.toLocaleString()} pending at the start` : ''}${r.limit ? ` · limit ${r.limit.toLocaleString()}` : ''}`}>
                <span className="text-xs">{r.processed !== null ? r.processed.toLocaleString() : '—'}{planned !== null ? ` / ${planned.toLocaleString()}` : ''}</span>
                {!!r.failed && <span className="text-[10px] text-red-600">{r.failed.toLocaleString()} failed</span>}
              </span>
            );
          },
          estimate: row => {
            const r: Run = row._r;
            return (
              <span className="text-xs whitespace-nowrap tabular-nums"
                title="With prompt caching (without caching) — an estimate, not a limit: the cap is the only limit">
                ≈ {fmtCost(r.est_cached)}{r.est_full !== null && <span className="text-grayscale-1100"> (≈ {fmtCost(r.est_full)} without)</span>}
              </span>
            );
          },
          cap: row => <span className="text-xs tabular-nums">{fmtCost((row._r as Run).cap)}</span>,
          actual: row => {
            const r: Run = row._r;
            const c = costOf(r);
            const live = r.running || LIVE.includes(r.status);
            const over = c !== null && r.est_cached !== null && r.est_cached > 0 ? c / r.est_cached : null;
            return (
              <span className="flex flex-col leading-tight whitespace-nowrap tabular-nums">
                <span className={`text-xs ${live ? 'text-grayscale-1100' : 'font-bold text-grayscale-1300'}`}>
                  {fmtCost(c)}{live && c !== null ? ' so far' : ''}
                </span>
                {!live && over !== null && (
                  <span className={`text-[10px] ${over > 1.1 ? 'text-amber-700' : 'text-grayscale-1100'}`}
                    title="Actual against the cached estimate">{Math.round(over * 100)}% of the estimate</span>
                )}
                {r.tokens !== null && <span className="text-[10px] text-grayscale-1100">{r.tokens.toLocaleString()} tokens</span>}
              </span>
            );
          },
          status: row => <StatusPill r={row._r} />,
          refreshed: row => {
            const rf = refreshOf(row._r);
            if (rf.state === 'ok') return <span className="text-xs text-green-700 whitespace-nowrap">✓{rf.rows !== null ? ` ${rf.rows.toLocaleString()} rows` : ''}</span>;
            if (rf.state === 'waiting') return <span className="text-xs text-grayscale-1100">at the end</span>;
            if (rf.state === 'failed') return <span className="text-xs text-red-600" title={rf.text}>failed</span>;
            return <span className="text-xs text-grayscale-1100" title={rf.text || undefined}>no</span>;
          },
        }}
        actions={row => (row.run_id ? (
          <Btn small onClick={() => ctx.goResults({ run: str(row.run_id) })}>Results</Btn>
        ) : null)} />
    </Section>
  );
};

export default HzRunPending;
